// Each anchor owns an explicit volume. Tall furniture claims the wall above it too.
export const ZONES = {
  seating: { position: [1.25, 0, .7], rotation: -Math.PI / 2 },
  lounge: { position: [1.8, 0, -1.65], rotation: .18 },
  table: { position: [-.55, 0, .45], rotation: Math.PI / 2 },
  media: { position: [-2.57, 0, -.75], rotation: Math.PI / 2 },
  screen: { position: [-2.87, 1.22, -.75], rotation: Math.PI / 2 },
  shelf: { position: [-2.77, 2.36, -.85], rotation: Math.PI / 2 },
  window: { position: [.72, .9, -2.98], rotation: 0 },
  windowStorage: { position: [.72, 0, -2.55], rotation: 0 },
  curtains: { position: [.72, .94, -2.82], rotation: 0 },
  wallAccent: { position: [-2.88, 1.5, 1.75], rotation: Math.PI / 2 },
  corner: { position: [-1.95, 0, -2.35], rotation: 0 },
  frontCorner: { position: [2.25, 0, 2.05], rotation: 0 },
  side: { position: [2.4, 0, -.72], rotation: 0 },
  sideTable: { position: [-1.15, 0, 2.15], rotation: 0 },
  pet: { position: [.05, 0, 2.3], rotation: -.3 },
  rug: { position: [-.05, .008, .8], rotation: 0 },
  tabletop: { support: ['coffeeTable'], height: .57 },
  consoleLeft: { support: ['tvStand'], height: .73, x: -.78 },
  consoleRight: { support: ['tvStand'], height: .73, x: .72 },
  shelfTop: { support: ['wallShelf'], height: .11 },
  sideTop: { support: ['stool', 'sideTable'], height: .62 },
  seatTop: { support: ['sofa', 'loveseat', 'sectional'], height: .57 },
}

const GROUPS = [
  [['sofa', 'loveseat', 'sectional'], ['seating']],
  [['armchair', 'chair', 'ottoman'], ['lounge']],
  [['coffeeTable'], ['table']], [['tvStand'], ['media']],
  [['wardrobe', 'bookshelf'], ['media', 'windowStorage']],
  [['desk'], ['windowStorage']], [['television'], ['screen']],
  [['wallShelf'], ['shelf']], [['window', 'archedWindow', 'wideWindow'], ['window']],
  [['curtains'], ['curtains']], [['picture', 'mirror', 'wallClock', 'wallGallery', 'wallSconce'], ['wallAccent', 'screen']],
  [['palm', 'rubberPlant', 'floorLamp', 'arcLamp'], ['corner', 'frontCorner']],
  [['snakePlant', 'basket', 'speaker', 'wastebasket', 'paperTrash'], ['side', 'frontCorner']],
  [['stool', 'sideTable'], ['sideTable']], [['cat', 'dog'], ['pet']], [['rug'], ['rug']],
  [['cushion', 'blanket'], ['seatTop']],
  [['books', 'bookStack', 'magazine', 'cactus', 'pottedPlant', 'tableLamp', 'vase', 'radio', 'storageBox'], ['tabletop', 'consoleLeft', 'consoleRight', 'shelfTop', 'sideTop']],
]
export const zonesFor = kind => GROUPS.find(([kinds]) => kinds.includes(kind))?.[1] || []
const tall = kind => ['wardrobe', 'bookshelf'].includes(kind)
const windowKind = kind => ['window', 'archedWindow', 'wideWindow'].includes(kind)

export function zoneOf(obj, room) {
  if (obj.zone && zonesFor(obj.kind).includes(obj.zone)) return obj.zone
  if (obj.parentId) {
    const parent = room.objects.find(p => p.id === obj.parentId)
    return zonesFor(obj.kind).find(zone => ZONES[zone].support?.includes(parent?.kind) && (zone !== 'consoleLeft' && zone !== 'consoleRight' || (obj.position[0] < 0) === (zone === 'consoleLeft')))
  }
  return zonesFor(obj.kind).filter(zone => !ZONES[zone].support).sort((a, b) => {
    const distance = z => Math.hypot(obj.position[0] - ZONES[z].position[0], obj.position[2] - ZONES[z].position[2])
    return distance(a) - distance(b)
  })[0]
}

function claims(kind, zone) {
  if (tall(kind) && zone === 'media') return ['media', 'screen', 'shelf']
  if (zone === 'windowStorage') return ['windowStorage', 'window', 'curtains', ...(tall(kind) ? ['lounge'] : [])]
  if (zone === 'window' || zone === 'curtains') return [zone]
  return [zone]
}

export function placementOptions(room, kind) {
  return zonesFor(kind).filter(zone => {
    const spec = ZONES[zone]
    if (spec.support) return room.objects.some(obj => spec.support.includes(obj.kind) && !obj.parentId)
    if (zone === 'curtains') return room.objects.some(obj => windowKind(obj.kind))
    return true
  })
}

export function planPlacement(room, kind, requestedZone, id) {
  const available = placementOptions(room, kind)
  if (!available.length) return { error: 'needsSupport' }
  if (requestedZone && requestedZone !== 'auto' && !available.includes(requestedZone)) return { error: 'needsSupport' }
  const collisions = zone => room.objects.filter(obj => obj.id !== id && claims(obj.kind, zoneOf(obj, room)).some(c => claims(kind, zone).includes(c)))
  const zone = available.includes(requestedZone) ? requestedZone : available.find(z => collisions(z).length === 0) || available[0]
  const spec = ZONES[zone]
  const parent = spec.support && room.objects.find(obj => spec.support.includes(obj.kind) && !obj.parentId)
  const obj = { id, kind, zone, position: parent ? [spec.x || 0, spec.height, 0] : [...spec.position], rotation: parent ? 0 : spec.rotation, parentId: parent?.id || null }
  const removed = new Set(collisions(zone).map(o => o.id))
  // Replacing a surface removes its contents. No floating lamps, books or curtains.
  let previousSize = -1
  while (previousSize !== removed.size) {
    previousSize = removed.size
    for (const existing of room.objects) if (removed.has(existing.parentId)) removed.add(existing.id)
  }
  const objects = room.objects.filter(o => !removed.has(o.id) && o.id !== id)
  if (objects.length >= 48) return { error: 'limit' }
  return { room: { ...room, objects: [...objects, obj] }, removed: [...removed], zone, object: obj }
}

export function isWindow(kind) { return windowKind(kind) }
